// The landing view's arithmetic: which projects are pressing, in what order, and
// what goes on the timeline.
//
// Here rather than in the component for the same reason project.ts is: `pnpm
// test` can cover it without rendering anything, and an ordering that is quietly
// wrong looks exactly like an ordering that is right.
//
// **The thresholds are the morning digest's**, deliberately — a firm date within
// seven days, a tentative one within fourteen, anything already past, any awake
// scheduling item (see SYSTEM.md, "The morning digest"). Two views of one
// database disagreeing about what is urgent is how you learn to trust neither,
// so the page and the 07:00 mail answer the question the same way. If those
// thresholds change, they change in both places or the board starts lying.

// `.ts` on the specifier, like the test files: this module imports *values*
// rather than only types, so node — which runs the tests directly, with no
// bundler — needs the real filename. `allowImportingTsExtensions` in tsconfig is
// what lets the same line compile for the browser.
import {
  daysUntil,
  isAwake,
  type Project,
  type ProjectCommitment,
  type ProjectDate,
} from './project.ts';

/** A project as the board holds it: its head, and where it lives in the wiki. */
export type BoardEntry = {
  /** `projects/ogham` — the directory, which is also the address of its page. */
  path: string;
  /** The directory name, for the `/reanalyze`-style kebab and as a fallback title. */
  slug: string;
  project: Project;
  /** When `state.md` was last committed, or null for a project that has never synced. */
  synced: number | null;
};

const FIRM_DAYS = 7;
const TENTATIVE_DAYS = 14;

/** Whether one date is close enough to be worth putting in front of someone. */
export function dateIsPressing(date: ProjectDate, today: Date): boolean {
  const days = daysUntil(date.date, today);
  if (days < 0) return true;
  return days <= (date.tentative ? TENTATIVE_DAYS : FIRM_DAYS);
}

/** Every date on this project that crosses a threshold, soonest — or latest overdue — first. */
export function pressingDates(project: Project, today: Date): ProjectDate[] {
  return project.dates
    .filter(d => dateIsPressing(d, today))
    .sort((a, b) => daysUntil(a.date, today) - daysUntil(b.date, today));
}

/** The unscheduled commitments that are actually being chased. Dormant ones are not. */
export function nagging(project: Project, today: Date): ProjectCommitment[] {
  return project.scheduling.filter(s => isAwake(s, today));
}

/**
 * How a project sorts on the board. Lower is more pressing; `null` is quiet.
 *
 * A dated commitment outranks an undated one at the same distance, which is the
 * one place this departs from the digest's flat list. The digest is a list of
 * things to notice; this is a page you read top to bottom, and something with a
 * day on it is the thing to look at first. An unscheduled item is chased daily
 * precisely *because* it has no clock — that makes it persistent, not imminent.
 */
export function rank(project: Project, today: Date): [number, number] | null {
  const dates = pressingDates(project, today);
  if (dates.length) return [0, daysUntil(dates[0].date, today)];
  if (nagging(project, today).length) return [1, 0];
  return null;
}

/** Whether this project wants pushing on rather than merely reading — see `/checkin`. */
export function isFlagged(entry: BoardEntry): boolean {
  return !entry.project.milestone;
}

/**
 * Every project in one list, most pressing first.
 *
 * This used to return three tiers — pressing, quiet, and a third for projects
 * with no milestone — and the page drew a section for each. Two things were
 * wrong with that and both are the same thing: a project with a deadline, a
 * missing milestone and a place on the calendar appeared three times on one
 * phone screen under three headings, and none of the tiers meant anything at
 * all to somebody whose wiki is a week old and has no dates in it yet.
 *
 * So the tiers become an order, and urgency becomes something a row says about
 * itself. What was a heading is now a word on the line it describes, which is
 * where it was always more useful.
 *
 * Ties break alphabetically, so the list is stable between loads — an order
 * that reshuffles under you is one nobody learns.
 */
export function ranked(entries: BoardEntry[], today: Date): BoardEntry[] {
  const at = new Map<string, [number, number] | null>();
  for (const entry of entries) at.set(entry.path, rank(entry.project, today));
  return [...entries].sort((a, b) => {
    const ra = at.get(a.path) ?? null;
    const rb = at.get(b.path) ?? null;
    if (ra && rb) return ra[0] - rb[0] || ra[1] - rb[1] || cmp(a, b);
    if (ra) return -1;
    if (rb) return 1;
    return cmp(a, b);
  });
}

function cmp(a: BoardEntry, b: BoardEntry): number {
  return a.project.title.localeCompare(b.project.title);
}

/** One row of the timeline: a commitment, and which project owes it. */
export type Deadline = {
  entry: BoardEntry;
  date: ProjectDate;
};

/**
 * Every dated commitment in the database, in the order they fall due.
 *
 * All of them, not a window. The convention is that a date comes out of
 * `project.md` once it has passed, so this list empties itself — and the whole
 * of it is currently three rows across eleven projects. A horizon would be
 * solving a problem this database does not have, and would hide the one thing
 * the timeline is best at: seeing two projects land in the same week.
 *
 * Past-due sorts to the top, because a date still sitting here after its day is
 * the database's one failure signal.
 */
export function timeline(entries: BoardEntry[], today: Date): Deadline[] {
  const rows: Deadline[] = [];
  for (const entry of entries) {
    for (const date of entry.project.dates) rows.push({ entry, date });
  }
  return rows.sort(
    (a, b) =>
      daysUntil(a.date.date, today) - daysUntil(b.date.date, today) ||
      a.entry.project.title.localeCompare(b.entry.project.title),
  );
}

/** One row of the second block: a commitment with no date, and whose it is. */
export type Unscheduled = {
  entry: BoardEntry;
  item: ProjectCommitment;
};

/**
 * The commitments with no day on them, under the timeline rather than in it.
 *
 * A list sorted by date cannot hold something that has no date without lying
 * about when it is. These are not less urgent — the digest chases them every
 * morning, which nothing on the timeline gets — they are unanchored, and the
 * obligation on one is to give it a date rather than to prepare for it.
 *
 * Dormant items come last and recessed. They are hidden from the digest
 * completely, and shown here for the reason the project page shows them: a nag
 * you cannot audit is how dormancy stops being honest.
 */
export function unscheduled(entries: BoardEntry[], today: Date): {
  waiting: Unscheduled[];
  parked: Unscheduled[];
} {
  const waiting: Unscheduled[] = [];
  const parked: Unscheduled[] = [];
  for (const entry of entries) {
    for (const item of entry.project.scheduling) {
      (isAwake(item, today) ? waiting : parked).push({ entry, item });
    }
  }
  const by = (a: Unscheduled, b: Unscheduled) =>
    a.entry.project.title.localeCompare(b.entry.project.title);
  return { waiting: waiting.sort(by), parked: parked.sort(by) };
}
